import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import PortfolioCompleteness from '../PortfolioCompleteness';
import PortfolioInsights from '../PortfolioInsights';
import ExportReports from '../ExportReports';
import HoldingsByCurrencyCard from './HoldingsByCurrencyCard';
import CashflowCalendarCard from '../cashflow/CashflowCalendarCard';
import EstateVaultStatusStrip from './EstateVaultStatusStrip';
import LiabilitiesCard from '../liabilities/LiabilitiesCard';
import NetWorthCard from '../networth/NetWorthCard';
import TaxYearPositionCard from '../tax/TaxYearPositionCard';
import { LockedFeaturePanel } from '../upgrade/LockedFeaturePanel';
import { INVESTMENT_TYPE_LABELS, getTypeColor, formatCurrency } from './constants';

/**
 * Composite section that renders the Liabilities management card and the
 * Statement of Net Worth read-only summary. Bumping `refreshKey` after any
 * liability mutation re-fetches the SoNW so totals stay in sync.
 */
const LiabilitiesCardSection = () => {
  const [refreshKey, setRefreshKey] = React.useState(0);
  return (
    <div className="space-y-6" data-testid="liabilities-section">
      <LiabilitiesCard onChange={() => setRefreshKey((k) => k + 1)} />
      <LockedFeaturePanel featureLabel="Net Worth" description="Your total assets minus liabilities across every currency and jurisdiction, in one number. Available on Portfolio Clarity.">
        <NetWorthCard refreshKey={refreshKey} />
      </LockedFeaturePanel>
    </div>
  );
};

const DashboardOverviewTab = ({
  portfolioSummary, investments, completeTypeChartData,
  counterpartyChartData, topCounterparty, topThirdPartyCounterparty,
  topConcentrationPct, isConcentrated, unspecifiedPct,
  setShowAddModal, handleEditInvestmentById, setActiveTab,
  user, refreshUser
}) => {
  if (!portfolioSummary) return null;

  // Detect whether the user holds investments in more than one currency.
  const distinctCurrencies = Array.from(new Set(
    (investments || [])
      .filter((inv) => inv.owner_type !== 'related')
      .map((inv) => (inv.currency || '').toUpperCase())
      .filter(Boolean)
  ));
  const isMultiCurrency = distinctCurrencies.length > 1;

  // Phase 2 FX: when the backend successfully converted everything, prefer the
  // display currency. Otherwise (FX feed unavailable, or single-currency user)
  // fall back to the row's native currency.
  const fxMeta = portfolioSummary.fx;
  const fxAvailable = !!fxMeta?.available;
  const displayCurrency = fxMeta?.display_currency || distinctCurrencies[0] || 'USD';
  // When FX is available, the backend totals (current_value, direct.current_value, …)
  // are already in displayCurrency. When unavailable and multi-currency, those
  // totals are raw mixed sums → still misleading, so we hide them.
  const showHeroNumber = !isMultiCurrency || fxAvailable;
  const formatDisplay = (val) => formatCurrency(val, displayCurrency);
  const fmtDate = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short' }); } catch { return iso; } };
  const unconverted = (fxMeta?.unconverted_currencies || []);

  return (
    <>
      {/* Total Investments Value Card */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
        <Card className="portfolio-card bg-linear-to-r from-emerald-500 to-blue-600 text-white flex-1">
          <CardHeader>
            <CardTitle className="text-xl sm:text-3xl font-serif font-bold">Total Investments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                {showHeroNumber ? (
                  <>
                    <div className="text-3xl sm:text-5xl font-mono font-bold mb-2">
                      <span data-sensitive="monetary">{formatDisplay(portfolioSummary.current_value)}</span>
                    </div>
                    <p className="text-emerald-50 text-sm sm:text-lg">
                      Across {portfolioSummary.total_investments} investment{portfolioSummary.total_investments !== 1 ? 's' : ''}
                      {isMultiCurrency && fxAvailable ? ` · ${distinctCurrencies.length} currencies converted to ${displayCurrency}` : ''}
                    </p>
                  </>
                ) : (
                  <div data-testid="multi-currency-headline">
                    <div className="text-xl sm:text-3xl font-serif font-semibold mb-2">
                      Held in {distinctCurrencies.length} currencies
                    </div>
                    <p className="text-emerald-50 text-sm sm:text-base max-w-2xl">
                      A single home-currency total needs live exchange rates. Until those are wired,
                      we show each currency on its own below — see <span className="font-semibold">Total Investments by Currency</span>.
                    </p>
                    <p className="text-emerald-100 text-xs mt-2">
                      Across {portfolioSummary.total_investments} investment{portfolioSummary.total_investments !== 1 ? 's' : ''} · {distinctCurrencies.join(' · ')}
                    </p>
                  </div>
                )}
              </div>
              {showHeroNumber && (
                <div className="grid grid-cols-2 gap-2 sm:gap-3 pt-4 border-t border-white/20">
                  <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                    <p className="text-xs text-emerald-100 uppercase tracking-wide">Direct</p>
                    <p className="text-lg sm:text-2xl font-bold mt-1">
                      <span data-sensitive="monetary">{formatDisplay(portfolioSummary?.direct_portfolio?.current_value || 0)}</span>
                    </p>
                    <p className="text-xs text-emerald-100 mt-1">{portfolioSummary?.direct_portfolio?.count || 0} investments</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                    <p className="text-xs text-blue-100 uppercase tracking-wide">Indirect</p>
                    <p className="text-lg sm:text-2xl font-bold mt-1">
                      <span data-sensitive="monetary">{formatDisplay(portfolioSummary?.indirect_portfolio?.current_value || 0)}</span>
                    </p>
                    <p className="text-xs text-blue-100 mt-1">{portfolioSummary?.indirect_portfolio?.count || 0} via entities</p>
                  </div>
                </div>
              )}
              {showHeroNumber && (
                <div className="bg-indigo-500/20 rounded-lg p-2 sm:p-3 border border-indigo-300/30">
                  <p className="text-xs text-indigo-100 uppercase tracking-wide">Related Assets</p>
                  <p className="text-base sm:text-lg font-semibold mt-1">
                    <span data-sensitive="monetary">{formatDisplay(portfolioSummary?.related_assets?.current_value || 0)}</span>
                  </p>
                  <p className="text-xs text-indigo-100 mt-1">{portfolioSummary?.related_assets?.count || 0} asset(s) - memorandum only</p>
                </div>
              )}
              {fxAvailable && (
                <div className="text-[11px] text-white/80 pt-2 border-t border-white/10" data-testid="fx-source-footnote">
                  Converted to {displayCurrency} using rates from {fxMeta.source} · {fmtDate(fxMeta.fetched_at)}
                  {unconverted.length > 0 && (
                    <span className="block mt-1 text-amber-100">
                      Note: {unconverted.join(', ')} not supported by current FX feed — shown in native value only.
                    </span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Holdings by Currency — Phase 1 (no FX conversion yet) */}
      <HoldingsByCurrencyCard investments={investments} />

      {/* Portfolio Completeness */}
      <PortfolioCompleteness onEditInvestment={handleEditInvestmentById} />

      {/* Investment Type Cards */}
      {completeTypeChartData.length > 0 && (
        <div>
          <h3 className="text-lg sm:text-2xl font-serif font-bold text-slate-900 mb-4 sm:mb-6">Investments by Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {completeTypeChartData.map((item, index) => (
              <Card 
                key={item.type} 
                className={`${getTypeColor(index)} text-white shadow-md hover:shadow-lg transition-shadow cursor-pointer`}
              >
                <CardHeader className="p-3 sm:pb-3">
                  <CardTitle className="text-xs sm:text-base font-medium capitalize leading-tight">
                    {INVESTMENT_TYPE_LABELS[item.type] || item.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <div className="text-lg sm:text-2xl font-mono font-bold mb-1">
                    <span data-sensitive="monetary">{formatCurrency(item.value)}</span>
                  </div>
                  <p className="text-white/90 text-xs sm:text-sm">
                    {item.count} investment{item.count !== 1 ? 's' : ''}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Indirect Investments by Entity */}
      {portfolioSummary?.indirect_portfolio?.entities?.length > 0 && (
        <div>
          <h3 className="text-lg sm:text-2xl font-serif font-bold text-slate-900 mb-4">Indirect Investments by Entity</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {portfolioSummary.indirect_portfolio.entities.map((entity) => (
              <Card key={entity.entity_id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="p-3 sm:pb-3">
                  <CardTitle className="text-base sm:text-lg font-semibold text-slate-900">
                    {entity.entity_name}
                  </CardTitle>
                  <Badge className="bg-blue-500 text-white w-fit">{entity.entity_type}</Badge>
                </CardHeader>
                <CardContent className="p-3">
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-slate-600">Country</p>
                      <p className="font-medium">{entity.entity_country}</p>
                    </div>
                    <div className="border-t pt-2">
                      <p className="text-xl sm:text-2xl font-bold text-blue-600">
                        <span data-sensitive="monetary">{formatCurrency(entity.total_value)}</span>
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        {entity.investment_count} investment{entity.investment_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Portfolio Insights */}
      {investments.length > 0 && (
        <LockedFeaturePanel featureLabel="Portfolio Insights" description="Diversification, risk and concentration observations tailored to your holdings. Available on Portfolio Clarity.">
          <PortfolioInsights investments={investments} portfolioSummary={portfolioSummary} />
        </LockedFeaturePanel>
      )}

      {/* Counterparty Concentration (risk lens) — only when there is data to show */}
      {counterpartyChartData && counterpartyChartData.length > 0 && (() => {
        const hasThirdParty = !!topThirdPartyCounterparty;
        const dataIncomplete = !hasThirdParty && unspecifiedPct > 0;
        let badge;
        if (isConcentrated) {
          badge = (
            <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-400 text-xs font-medium" data-testid="overview-concentration-warning-badge">
              High concentration risk
            </Badge>
          );
        } else if (dataIncomplete) {
          badge = (
            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs font-medium" data-testid="overview-concentration-incomplete-badge">
              Data incomplete
            </Badge>
          );
        } else {
          badge = (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 text-xs font-medium" data-testid="overview-concentration-healthy-badge">
              Within healthy range
            </Badge>
          );
        }
        return (
          <Card
            className={`portfolio-card ${isConcentrated ? 'border-amber-300 bg-amber-50/30' : ''}`}
            data-testid="overview-counterparty-concentration-card"
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  {isConcentrated && <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />}
                  <CardTitle className="text-base sm:text-xl font-serif">
                    Counterparty Concentration
                  </CardTitle>
                </div>
                {badge}
              </div>
            </CardHeader>
            <CardContent>
              {isConcentrated ? (
                <p className="text-sm text-amber-900 mb-3">
                  <span className="font-semibold">{topThirdPartyCounterparty.name}</span> holds or issues{' '}
                  <span className="font-semibold">{topConcentrationPct.toFixed(1)}%</span> of your portfolio.
                  Concentration above 25% in a single third-party counterparty raises your single-point-of-failure risk.
                </p>
              ) : hasThirdParty ? (
                <p className="text-sm text-slate-700 mb-3">
                  Top third-party counterparty: <span className="font-semibold">{topThirdPartyCounterparty.name}</span> at{' '}
                  <span className="font-semibold">{topConcentrationPct.toFixed(1)}%</span> of your portfolio
                  — below the 25% concentration threshold.
                </p>
              ) : dataIncomplete ? (
                <p className="text-sm text-slate-700 mb-3">
                  No third-party counterparties named yet — add the institution, issuer or fund manager on your holdings so we can compute true concentration.
                </p>
              ) : (
                <p className="text-sm text-slate-700 mb-3">
                  Your portfolio is entirely self-custodied (real estate, gold) — no third-party counterparty risk to flag.
                </p>
              )}
              <div className="space-y-1.5" data-testid="overview-counterparty-top3">
                {counterpartyChartData.slice(0, 3).map((cp) => {
                  const isTopRow = isConcentrated && cp.name === topThirdPartyCounterparty?.name;
                  const isSelfCustodied = cp.isThirdParty === false && !cp.isUnspecified;
                  const isUnspecified = cp.isUnspecified === true;
                  const dotClass = isTopRow ? 'bg-amber-500'
                    : isUnspecified ? 'bg-slate-300'
                    : isSelfCustodied ? 'bg-slate-400'
                    : 'bg-emerald-500';
                  const labelClass = isTopRow ? 'text-amber-900 font-medium'
                    : isUnspecified ? 'text-slate-400 italic'
                    : isSelfCustodied ? 'text-slate-500 italic'
                    : 'text-slate-800';
                  return (
                    <div key={cp.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotClass}`} />
                        <span className={`truncate ${labelClass}`}>
                          {cp.name}
                        </span>
                      </div>
                      <span className={`font-mono ${isTopRow ? 'text-amber-900 font-semibold' : 'text-slate-600'}`}>
                        {cp.pctOfTotal.toFixed(1)}%
                      </span>
                    </div>
                  );
                })}
              </div>
              {unspecifiedPct > 0 && !dataIncomplete && (
                <div
                  className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                  data-testid="overview-counterparty-unspecified-note"
                >
                  <span className="font-semibold">{unspecifiedPct.toFixed(1)}%</span> of your portfolio has no counterparty recorded.
                  Add the institution, issuer or fund manager on those holdings to surface their true concentration.
                </div>
              )}
              {counterpartyChartData.length > 3 && setActiveTab && (
                <button
                  type="button"
                  onClick={() => setActiveTab('allocation')}
                  className="mt-3 text-xs text-blue-700 hover:text-blue-900 underline underline-offset-2"
                  data-testid="overview-counterparty-view-full"
                >
                  View full breakdown in Allocation tab →
                </button>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Continuity System status — calm, single-line, only when there's something to convey */}
      <EstateVaultStatusStrip onGoToSettings={() => setActiveTab && setActiveTab('settings')} />

      {/* Cashflow Calendar — forward-looking projection of expected receipts */}
      {investments.length > 0 && (
        <CashflowCalendarCard user={user} refreshUser={refreshUser} />
      )}

      {/* Liabilities — Statement of Net Worth Phase 1 (manage debts) */}
      <LiabilitiesCardSection />

      {/* Current Tax Year Position — realised so far + expected before year-end + unrealised */}
      {investments.length > 0 && <TaxYearPositionCard />}

      {/* Export & Reports */}
      {investments.length > 0 && (
        <div data-tour="export">
          <ExportReports
            investments={investments}
            portfolioSummary={portfolioSummary}
            onGoToSettings={setActiveTab ? () => setActiveTab('settings') : undefined}
          />
        </div>
      )}
    </>
  );
};

export default DashboardOverviewTab;
